import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { pocketbimaFaqs, generalInsuranceFaqs, serviceFaqs } from "../data/faqs";
import { PHONE_TEL, PHONE_DISPLAY } from "../lib/site";

const groups = [
  { title: "About Pocket Bima Insurance Service", faqs: pocketbimaFaqs, to: null },
  { title: "Insurance Basics", faqs: generalInsuranceFaqs, to: null },
  { title: "Motor Insurance", faqs: serviceFaqs.motor.slice(0, 4), to: "/motor-insurance" },
  { title: "Health Insurance", faqs: serviceFaqs.health.slice(0, 4), to: "/health-insurance" },
  { title: "Life & Term Insurance", faqs: serviceFaqs.life.slice(0, 4), to: "/life-insurance" },
  { title: "Business Insurance", faqs: serviceFaqs.business.slice(0, 4), to: "/business-insurance" },
  { title: "Travel Insurance", faqs: serviceFaqs.travel.slice(0, 4), to: "/travel-insurance" },
];

export default function FaqPage() {
  useEffect(() => {
    const all = groups.flatMap((g) => g.faqs);
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: all.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  return (
    <main data-testid="faq-page">
      <PageHero
        eyebrow="Help Centre"
        title="Insurance Questions,"
        highlight="Simply Answered."
        subtitle="Plain-language answers to the most common insurance questions — no jargon, no confusion. From premiums to claims, here's what people ask us most."
      />

      <section className="bg-ice py-20" data-testid="faq-groups-section">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          {groups.map((g, gi) => (
            <Reveal key={g.title} delay={0.05}>
              <div className={gi === 0 ? "" : "mt-14"} data-testid={`faq-group-${g.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="font-display text-2xl font-extrabold tracking-tight text-navy">{g.title}</h2>
                  {g.to && (
                    <Link to={g.to} className="flex items-center gap-1 text-sm font-bold text-mint-600" data-testid={`faq-group-link-${g.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      All {g.title} FAQs <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </div>
                <Accordion type="single" collapsible className="mt-6 space-y-4">
                  {g.faqs.map((f, i) => (
                    <AccordionItem
                      key={i}
                      value={`g${gi}-${i}`}
                      className="rounded-2xl border border-navy/10 bg-white px-6 shadow-soft transition-shadow duration-200 data-[state=open]:shadow-lift"
                      data-testid={`faq-page-item-${gi}-${i}`}
                    >
                      <AccordionTrigger className="py-5 text-left font-display text-base font-bold text-navy hover:no-underline [&>svg]:text-mint-600">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="pb-5 text-sm leading-relaxed text-charcoal/80">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.1}>
            <div className="mt-16 rounded-[2rem] bg-navy p-8 text-center sm:p-12" data-testid="faq-cta-card">
              <h2 className="font-display text-2xl font-extrabold text-white sm:text-3xl">
                Didn't find your answer?
              </h2>
              <p className="mx-auto mt-3 max-w-md text-sm text-white/70 sm:text-base">
                Talk to a real insurance professional — no obligation, no charge.
              </p>
              <div className="mt-7 flex flex-wrap justify-center gap-4">
                <a href={PHONE_TEL} className="flex items-center gap-2 rounded-full bg-mint px-7 py-3.5 text-sm font-bold text-white shadow-mint transition-all hover:-translate-y-0.5 hover:bg-mint-600" data-testid="faq-page-call">
                  <Phone className="h-4 w-4" /> Call {PHONE_DISPLAY}
                </a>
                <Link to="/contact" className="flex items-center gap-2 rounded-full border-2 border-white/25 px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-white/10" data-testid="faq-page-quote">
                  Get a Free Quote
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
